/**
 * Minimal, dependency-free ZIP writer (STORE method — no compression).
 *
 * Used by the Admin "Full Account Data Reset" backup flow to bundle several
 * CSV files into a single download. We deliberately avoid a third-party zip
 * dependency: STORE-method archives are simple and well-understood, and the
 * resulting file opens in every OS archive tool and spreadsheet app.
 *
 * Only used by admin export flows — never imported by production screens.
 */

// ── CRC-32 (IEEE 802.3) ─────────────────────────────────────────────────────
const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  return table;
})();

function crc32(bytes) {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

// ── DOS date/time encoding ──────────────────────────────────────────────────
function dosDateTime(date) {
  const d = date instanceof Date && !isNaN(date) ? date : new Date();
  const year = Math.max(0, d.getFullYear() - 1980);
  const time = (d.getHours() << 11) | (d.getMinutes() << 5) | (Math.floor(d.getSeconds() / 2));
  const dosDate = (year << 9) | ((d.getMonth() + 1) << 5) | d.getDate();
  return { time: time & 0xffff, date: dosDate & 0xffff };
}

/**
 * Build a ZIP archive (STORE method) from a list of files.
 * @param {{name: string, content: string}[]} files
 * @returns {Blob}
 */
export function makeZip(files) {
  const encoder = new TextEncoder();
  const { time, date } = dosDateTime(new Date());

  const localParts = [];
  const centralParts = [];
  let offset = 0;

  for (const file of files) {
    const nameBytes = encoder.encode(file.name);
    const dataBytes = encoder.encode(file.content ?? '');
    const crc = crc32(dataBytes);
    const size = dataBytes.length;

    // ── Local file header (30 bytes + name) ──
    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true); // local file header signature
    local.setUint16(4, 20, true);         // version needed to extract (2.0)
    local.setUint16(6, 0x0800, true);     // general purpose flag: UTF-8 names
    local.setUint16(8, 0, true);          // compression method: 0 = store
    local.setUint16(10, time, true);
    local.setUint16(12, date, true);
    local.setUint32(14, crc, true);
    local.setUint32(18, size, true);      // compressed size
    local.setUint32(22, size, true);      // uncompressed size
    local.setUint16(26, nameBytes.length, true);
    local.setUint16(28, 0, true);         // extra field length

    localParts.push(new Uint8Array(local.buffer), nameBytes, dataBytes);

    // ── Central directory header (46 bytes + name) ──
    const central = new DataView(new ArrayBuffer(46));
    central.setUint32(0, 0x02014b50, true); // central dir signature
    central.setUint16(4, 20, true);          // version made by
    central.setUint16(6, 20, true);          // version needed
    central.setUint16(8, 0x0800, true);      // flags: UTF-8
    central.setUint16(10, 0, true);          // method: store
    central.setUint16(12, time, true);
    central.setUint16(14, date, true);
    central.setUint32(16, crc, true);
    central.setUint32(20, size, true);
    central.setUint32(24, size, true);
    central.setUint16(28, nameBytes.length, true);
    central.setUint16(30, 0, true);          // extra field length
    central.setUint16(32, 0, true);          // comment length
    central.setUint16(34, 0, true);          // disk number start
    central.setUint16(36, 0, true);          // internal attributes
    central.setUint32(38, 0, true);          // external attributes
    central.setUint32(42, offset, true);     // offset of local header

    centralParts.push(new Uint8Array(central.buffer), nameBytes);

    offset += 30 + nameBytes.length + size;
  }

  const centralSize = centralParts.reduce((s, p) => s + p.length, 0);

  // ── End of central directory record (22 bytes) ──
  const eocd = new DataView(new ArrayBuffer(22));
  eocd.setUint32(0, 0x06054b50, true);   // EOCD signature
  eocd.setUint16(4, 0, true);            // disk number
  eocd.setUint16(6, 0, true);            // disk with central dir
  eocd.setUint16(8, files.length, true); // entries on this disk
  eocd.setUint16(10, files.length, true);// total entries
  eocd.setUint32(12, centralSize, true); // central dir size
  eocd.setUint32(16, offset, true);      // central dir offset
  eocd.setUint16(20, 0, true);           // comment length

  return new Blob([...localParts, ...centralParts, new Uint8Array(eocd.buffer)], {
    type: 'application/zip',
  });
}

/** Trigger a browser download of a Blob. */
export function downloadBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
